import { ApolloClient, ApolloLink, InMemoryCache, Observable } from "@apollo/client";
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { QueryClient } from "react-query";
import { environment } from "./environment";
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { getAuth } from 'firebase/auth';
import { RetryLink } from '@apollo/client/link/retry';
// import { AppContext, environment } from "./components/AppContext";

// Global navigation function for use outside React components
let globalNavigate: ((path: string) => void) | null = null;
let isHandlingAuthError = false;

export const setGlobalNavigate = (navigate: (path: string) => void) => {
  globalNavigate = navigate;
};

export const queryClient = new QueryClient({ });

export const uri = (() => {
  if (environment === 'emulator') {
    // Prefer the hosting emulator (`:5010`) so the `firebase.json` rewrite
    // `/api/** -> api` kicks in. This matches the planapus setup: every
    // GraphQL / auth / admin call is same-origin via hosting, so we don't
    // need to open the functions emulator to cross-origin traffic.
    //
    // If the page itself is already being served from the hosting emulator
    // (the normal local dev flow: `open http://localhost:5010`), we can use
    // a relative URL and inherit the current origin. Otherwise we fall back
    // to hitting the hosting emulator by hostname on :5010.
    const {hostname, port} = window.location;
    if (port === '5010') return '/api/graphql';
    return `http://${hostname}:5010/api/graphql`;
  }

  // Prod: web app is hosted at goodshowhq.com (and kidtube-887cb.web.app);
  // api at api.goodshowhq.com — two separate Firebase Hosting sites.
  // Absolute URL is required because the web site no longer carries an
  // /api/** rewrite (clean per-host separation). CORS is handled
  // server-side via the CORS_ALLOWED_ORIGINS env on the function.
  return 'https://api.goodshowhq.com/graphql';
})();

// const auth = getAuth();
// const httpLink = createHttpLink({ uri });

// https://www.apollographql.com/docs/react/api/link/apollo-link-retry/
// const retryLink = new RetryLink({
//   delay: {
//     initial: 2000,
//     max: 5,
//     jitter: true
//   },
//   attempts: {
//     max: 5,
//     retryIf: (error, _operation) => {
//       // console.log(error, _operation);
//       return !!error;
//     }
//   }
// });

const batchLink = new BatchHttpLink({
  uri, // Replace with your GraphQL endpoint
  batchMax: 10, // Max number of operations per batch
  batchInterval: 10, // Time (in ms) to wait before batching
});

// Create auth link to add Firebase ID token to requests
const authLink = setContext(async (_, { headers }) => {
  try {
    const auth = getAuth();
    
    // Wait for auth state to be determined if it's still initializing
    if (auth.currentUser === null) {
      await new Promise<void>((resolve) => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
          unsubscribe();
          resolve();
        });
      });
    }
    
    const currentUser = auth.currentUser;
    
    if (currentUser) {
      // getIdToken() automatically refreshes if the token is expired or about to expire
      // Pass 'false' to only force refresh if expired (default behavior)
      const idToken = await currentUser.getIdToken(false);
      if (idToken) {
        console.log('GraphQL request with auth token for user:', currentUser.uid);
        return {
          headers: {
            ...headers,
            authorization: `Bearer ${idToken}`,
          }
        };
      } else {
        console.warn('GraphQL request: Failed to get ID token for authenticated user');
      }
    } else {
      console.warn('GraphQL request: No authenticated user, sending request without auth token');
    }
  } catch (error) {
    console.error('Error getting Firebase ID token:', error);
  }
  
  return {
    headers
  };
});

// Create cache instance first.
//
// typePolicies — some of our GraphQL types have an `id` field that is NOT
// globally unique (it's a logical id that's shared across versions). Without
// explicit composite keyFields, Apollo's default `__typename:id` cache key
// merges multiple rows into one — the bug we saw: evolving a prompt made
// v1.0.0 and v1.0.1 share the same cache entry, so the UI rendered two
// copies of whichever was written last and dropped the other.
const cache = new InMemoryCache({
  typePolicies: {
    AgentPromptEntry: {
      // Composite key — versions of the same prompt id are separate rows.
      keyFields: ['kind', 'id', 'version'],
    },
    EvalReport: {
      keyFields: ['id'],
    },
    BullmqJobEntry: {
      keyFields: ['id'],
    },
    // The rest (Video, AgentScore, …) keep the default __typename:id policy.
  },
});

// Error link to handle authentication errors globally
const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    // Check for token expiration errors
    const hasTokenExpiredError = graphQLErrors.some(({ message, extensions }) => {
      return extensions?.code === 'UNAUTHENTICATED' || 
             message.includes('id-token-expired') || 
             message.includes('Firebase ID token has expired');
    });

    if (hasTokenExpiredError && !isHandlingAuthError) {
      isHandlingAuthError = true;
      console.log('Token expired, attempting to refresh...');
      
      // Return an observable that will retry the operation with a fresh token
      return new Observable(observer => {
        const auth = getAuth();
        const currentUser = auth.currentUser;
        
        if (currentUser) {
          // Force refresh the token
          currentUser.getIdToken(true)
            .then(freshToken => {
              console.log('Token refreshed successfully, retrying operation');
              
              // Update the authorization header with the fresh token
              const oldHeaders = operation.getContext().headers;
              operation.setContext({
                headers: {
                  ...oldHeaders,
                  authorization: `Bearer ${freshToken}`,
                },
              });
              
              // Reset the flag
              isHandlingAuthError = false;
              
              // Retry the operation with the fresh token
              const subscriber = forward(operation).subscribe({
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
              });
              
              return () => subscriber.unsubscribe();
            })
            .catch(async (refreshError) => {
              console.error('Failed to refresh token:', refreshError);
              isHandlingAuthError = false;
              
              // If refresh fails, sign out and redirect to login
              cache.reset();
              
              try {
                await auth.signOut();
                console.log('Signed out user due to authentication error');
              } catch (signOutError) {
                console.error('Error signing out:', signOutError);
              }
              
              // Redirect to auth page
              if (globalNavigate) {
                globalNavigate('/auth');
              } else {
                window.location.hash = '/auth';
              }
              
              observer.error(refreshError);
            });
        } else {
          // No user logged in, redirect to auth
          isHandlingAuthError = false;
          console.log('No authenticated user, redirecting to login');
          
          if (globalNavigate) {
            globalNavigate('/auth');
          } else {
            window.location.hash = '/auth';
          }
          
          observer.error(new Error('Not authenticated'));
        }
      });
    }
    
    // Log other GraphQL errors
    graphQLErrors.forEach(({ message, locations, path }) => {
      if (!message.includes('id-token-expired')) {
        console.error(`GraphQL error: Message: ${message}, Location: ${locations}, Path: ${path}`);
      }
    });
  }

  if (networkError) {
    console.error(`Network error: ${networkError}`);
  }
});

// Add retry link for network failures
const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true
  },
  attempts: {
    max: 3,
    retryIf: (error, _operation) => {
      // Don't retry on authentication errors that we handle in errorLink
      const hasAuthError = error && error.graphQLErrors?.some((e: any) => 
        e.extensions?.code === 'UNAUTHENTICATED' || 
        e.message?.includes('id-token-expired')
      );
      return !hasAuthError && !!error;
    }
  }
});

export const client = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, retryLink, batchLink]),
  cache
});